// api.js - Secure API client
import { getToken, getuserId } from "./auth";
import { nowIso } from "./appDateTime";
import { appendCsvQueryParam as appendCsvQueryParamHelper } from "./apiQuery";
import { fetchStudies as fetchStudiesFromStudyApi } from "./apiStudy";
import {
  API_BASE_URL,
  adobeSignAuth,
  adobeSignAPI,
  initiateAdobeSignAuth,
  handleAdobeSignCallback,
  isAdobeSignAuthenticated,
  logoutAdobeSign,
  fetchAPI
} from "./apiCore";

// Re-export adobe sign stuff in case they were imported from api.js
export * from "./apiPatient";
export * from './apiEncounter';
export * from './apiUser';
export * from './apiBiospecimen';
export * from './apiFollowUp';
export * from './apiProvider';
export * from './apiStudy';
export * from './apiConsent';
export * from './apiGeneral';
export * from './apiReferenceData';
export * from './apiConsentForms';
export * from './apiEntityAdmin';
export {
  initiateAdobeSignAuth,
  handleAdobeSignCallback,
  isAdobeSignAuthenticated,
  logoutAdobeSign,
  fetchAPI
};










// Update Work Phone
export const updateWorkPhone = async (workPhoneNumber) => {
  const userId = getuserId();
  if (!userId) {
    throw new Error("User ID not available");
  }

  const trimmedPhoneNumber = workPhoneNumber.trim();

  return fetchAPI(`users/${userId}/work-phone`, "PUT", {}, trimmedPhoneNumber);
};











































// Fetch IRB Studies
export const fetchStudies = async () => {
  return fetchStudiesFromStudyApi();
};












export const appendCsvQueryParam = appendCsvQueryParamHelper;









// ADMIN PROFILE API FUNCTIONS

// Fetch admin profile statistics
export const fetchAdminStats = async () => {
  try {
    return await fetchAPI("admin/stats", "GET");
  } catch (error) {
    console.error("Error fetching admin stats:", error);
    return {
      totalUsers: 0,
      totalSamples: 0,
      totalPatients: 0,
      totalPrograms: 0,
      totalConsentForms: 0,
      outstandingReviews: 0,
    };
  }
};

// Fetch admin profile data
export const fetchAdminProfile = async () => {
  try {
    return await fetchAPI("admin/profile", "GET");
  } catch (error) {
    console.error("Error fetching admin profile:", error);
    return null;
  }
};

// Fetch outstanding reviews for consent management
export const fetchOutstandingReviews = async () => {
  try {
    return await fetchAPI("admin/consent/outstanding-reviews", "GET");
  } catch (error) {
    console.error("Error fetching outstanding reviews:", error);
    return [];
  }
};



// Search admin patients with advanced filtering
export const searchAdminPatients = async (request) => {
  try {
    return await fetchAPI("admin/patients/search", "POST", {}, request);
  } catch (error) {
    console.error("Error searching admin patients:", error);
    throw error;
  }
};


// Fetch physician list for program directory
export const fetchPhysicianList = async () => {
  try {
    return await fetchAPI("admin/programs/physicians", "GET");
  } catch (error) {
    console.error("Error fetching physician list:", error);
    return [];
  }
};













// Fetch active studies for dashboard
export const fetchActiveStudies = async () => {
  try {
    const studies = await fetchStudies();
    return studies.map((study) => {
      const irbStudy = study.irbStudy || study.id || "";
      const studyName = study.studyName || study.name || irbStudy;
      return {
        id: study.id || irbStudy,
        irbStudy,
        studyName,
        name: `${irbStudy}: ${studyName}`,
      };
    });
  } catch (error) {
    console.error("Error fetching active studies:", error);
    return [];
  }
};
















// Fetch enrollment statistics
export const fetchEnrollmentStats = async () => {
  try {
    return await fetchAPI("admin/enrollment/stats", "GET");
  } catch (error) {
    console.error("Error fetching enrollment stats:", error);
    return {
      totalPatients: 10,
    };
  }
};

// Fetch biospecimen statistics
export const fetchBiospecimenStats = async () => {
  try {
    return await fetchAPI("admin/biospecimen/stats", "GET");
  } catch (error) {
    console.error("Error fetching biospecimen stats:", error);
    return {
      totalSamplesCollected: 100,
      totalAliquotsProcessed: 900,
      totalAliquotsShipped: 80,
    };
  }
};

// Fetch outstanding surveys
export const fetchOutstandingSurveys = async () => {
  try {
    return await fetchAPI("admin/surveys/outstanding", "GET");
  } catch (error) {
    console.error("Error fetching outstanding surveys:", error);
    return [];
  }
};

// Fetch outstanding todos
export const fetchOutstandingTodos = async () => {
  try {
    return await fetchAPI("admin/todos/outstanding", "GET");
  } catch (error) {
    console.error("Error fetching outstanding todos:", error);
    return [];
  }
};


// REDCap API Functions
export const fetchRedcapSyncHistory = async (projectKey = "default|1730") => {
  try {
    return await fetchAPI("admin/redcap/sync/history", "GET", { redcapProjectKey: projectKey });
  } catch (error) {
    console.error("Error fetching REDCap sync history:", error);
    throw error;
  }
};

export const triggerRedcapSync = async (params) => {
  try {
    return await fetchAPI("admin/redcap/sync", "POST", {}, params);
  } catch (error) {
    console.error("Error triggering REDCap sync:", error);
    throw error;
  }
};

export const updateRedcapInstrumentAbbreviation = async (instrumentName, abbreviation) => {
  try {
    return await fetchAPI(
      `admin/redcap/instruments/${encodeURIComponent(instrumentName)}/abbreviation`,
      "PATCH",
      {},
      { abbreviation }
    );
  } catch (error) {
    console.error("Error updating REDCap instrument abbreviation:", error);
    throw error;
  }
};

export const fetchRedcapAnswerInstrumentSummary = async ({
  redcapProjectKey = "default|1730",
  instrumentName,
  includeUnsupported = false,
} = {}) => {
  if (!instrumentName) {
    throw new Error("instrumentName is required");
  }

  try {
    return await fetchAPI(
      `integrations/redcap/answer-query/instruments/${encodeURIComponent(instrumentName)}/summary`,
      "GET",
      {
        redcapProjectKey,
        includeUnsupported,
      }
    );
  } catch (error) {
    console.error("Error fetching REDCap answer summary:", error);
    throw error;
  }
};

export const queryRedcapAnswerSubjects = async (request) => {
  try {
    return await fetchAPI("integrations/redcap/answer-query/subjects", "POST", {}, request);
  } catch (error) {
    console.error("Error querying REDCap answer subjects:", error);
    throw error;
  }
};

export const fetchRedcapDashboard = async (params = {}) => {
  const {
    redcapProjectKey = "default|1730",
    page = 0,
    size = 20,
    formId = "",
    name = "",
    status = "",
    search = "",
    sortBy = "redcapFormId",
    sortDirection = "asc",
  } = params;

  try {
    const queryParams = {
      redcapProjectKey,
      page,
      size,
      sortBy,
      sortDirection,
    };

    if (search.trim()) {
       queryParams.search = search.trim();
    } else if (status.trim()) {
       queryParams.search = status.trim();
    } else if (formId.trim()) {
       queryParams.search = formId.trim();
    } else if (name.trim()) {
       queryParams.search = name.trim();
    }

    return await fetchAPI("integrations/redcap/completeness-dashboard", "GET", queryParams);
  } catch (error) {
    console.error("Error fetching REDCap dashboard:", error);
    return {
      records: [],
      instrumentColumns: [],
      totalElements: 0,
      totalPages: 0,
      pageNumber: page,
      pageSize: size,
    };
  }
};

// Biobank Projects API Functions
export const fetchProjects = async (params = {}) => {
  const { projectName, irbNum } = params;
  const queryParams = {};
  if (projectName) queryParams.projectName = projectName;
  if (irbNum) queryParams.irbNum = irbNum;
  
  try {
    return await fetchAPI("biobank/projects", "GET", queryParams);
  } catch (error) {
    console.error("Error fetching biobank projects:", error);
    throw error;
  }
};

export const fetchProjectById = async (projectId) => {
  try {
    return await fetchAPI(`biobank/projects/${encodeURIComponent(projectId)}`, "GET");
  } catch (error) {
    console.error(`Error fetching project ${projectId}:`, error);
    throw error;
  }
};

export const createProject = async (projectData) => {
  try {
    return await fetchAPI("biobank/projects", "POST", {}, projectData);
  } catch (error) {
    console.error("Error creating project:", error);
    throw error;
  }
};

export const patchProject = async (projectId, projectData) => {
  try {
    return await fetchAPI(`biobank/projects/${encodeURIComponent(projectId)}`, "PATCH", {}, projectData);
  } catch (error) {
    console.error(`Error patching project ${projectId}:`, error);
    throw error;
  }
};

// Adobe Sign API Functions
export const adobeSign = {
  // Authentication
  initiateAuth: initiateAdobeSignAuth,
  handleCallback: handleAdobeSignCallback,
  isAuthenticated: isAdobeSignAuthenticated,
  logout: logoutAdobeSign,
  getAuthorizationUrl: () => adobeSignAuth.getAuthorizationUrl(),
  exchangeCodeForTokens: (code) => adobeSignAuth.exchangeCodeForTokens(code),
  generateCodeVerifier: () => adobeSignAuth.generateCodeVerifier(),
  generateCodeChallenge: (codeVerifier) =>
    adobeSignAuth.generateCodeChallenge(codeVerifier),

  // User operations
  getUserInfo: () => adobeSignAPI.getUserInfo(),

  // Agreement operations
  createAgreement: (agreementData) =>
    adobeSignAPI.createAgreement(agreementData),
  getAgreement: (agreementId) => adobeSignAPI.getAgreement(agreementId),
  getAgreementStatus: (agreementId) =>
    adobeSignAPI.getAgreementStatus(agreementId),
  getAgreementDocuments: (agreementId) =>
    adobeSignAPI.getAgreementDocuments(agreementId),
  downloadAgreementDocument: (agreementId, documentId) =>
    adobeSignAPI.downloadAgreementDocument(agreementId, documentId),
  cancelAgreement: (agreementId, comment) =>
    adobeSignAPI.cancelAgreement(agreementId, comment),
  getUserAgreements: (params) => adobeSignAPI.getUserAgreements(params),

  // Library document operations
  createLibraryDocument: (documentData) =>
    adobeSignAPI.createLibraryDocument(documentData),
  getLibraryDocuments: () => adobeSignAPI.getLibraryDocuments(),

  // Webhook operations
  getWebhooks: () => adobeSignAPI.getWebhooks(),
  createWebhook: (webhookData) => adobeSignAPI.createWebhook(webhookData),

  // Template operations
  getTemplates: () => adobeSignAPI.getTemplates(),
  getTemplate: (templateId) => adobeSignAPI.getTemplate(templateId),
  createAgreementFromTemplate: (templateId, agreementData) =>
    adobeSignAPI.createAgreementFromTemplate(templateId, agreementData),

  // Advanced agreement operations
  getAgreementFormFields: (agreementId) =>
    adobeSignAPI.getAgreementFormFields(agreementId),
  updateAgreementFormFields: (agreementId, formFields) =>
    adobeSignAPI.updateAgreementFormFields(agreementId, formFields),
  getAgreementAuditTrail: (agreementId) =>
    adobeSignAPI.getAgreementAuditTrail(agreementId),
  getAgreementSigningUrls: (agreementId) =>
    adobeSignAPI.getAgreementSigningUrls(agreementId),
};
